import jsPDF from "jspdf";

// ─── Helpers ────────────────────────────────────────────────────────────────

const sanitize = (str) =>
  String(str || "")
    .replace(/[\x00-\x08\x0B\x0C\x0E-\x1F\x7F]/g, "")
    .trim();

const wrapText = (pdf, text, maxWidth) => {
  if (!text) return [""];
  return pdf.splitTextToSize(String(text), maxWidth);
};

const getInitials = (name) =>
  String(name || "")
    .split(" ")
    .slice(0, 2)
    .map((w) => [...w][0] || "")
    .join("")
    .toUpperCase();

const addWatermark = (pdf, pageWidth, pageHeight) => {
  pdf.saveGraphicsState();
  pdf.setGState(new pdf.GState({ opacity: 0.07 }));
  pdf.setFont("times", "bold");
  pdf.setFontSize(70);
  pdf.setTextColor(120, 120, 120);
  pdf.text("Inmate Inbox", pageWidth / 2 + 20, pageHeight / 2 + 50, {
    align: "center",
    angle: 35,
  });
  pdf.restoreGraphicsState();
};

const addPageNumbers = (pdf, pageNumber, totalPages, pageWidth, pageHeight) => {
  pdf.setFontSize(9);
  pdf.setFont("helvetica", "normal");
  pdf.setTextColor(150, 150, 150);
  pdf.text(
    `Page ${pageNumber} of ${totalPages}`,
    pageWidth - 14,
    pageHeight - 8,
    { align: "right" }
  );
};

// ─── Emoji Helpers ────────────────────────────────────────────────────────────

const isFamilyEmojiFour = (emoji) => [...emoji].length >= 7;
const isFamilyEmoji = (emoji) => [...emoji].length >= 5;

const splitString = (str) => {
  if (!str) return [];

  const emojiRegex =
    /(\p{Emoji_Presentation}|\p{Extended_Pictographic})(?:\u200D(?:\p{Emoji_Presentation}|\p{Extended_Pictographic})[\uFE0F\u20E3]?)*/gu;

  const result = [];
  let lastIndex = 0;
  let match;

  emojiRegex.lastIndex = 0;
  while ((match = emojiRegex.exec(str)) !== null) {
    if (match.index > lastIndex) {
      result.push({ type: "text", value: str.slice(lastIndex, match.index) });
    }
    result.push({ type: "emoji", value: match[0] });
    lastIndex = emojiRegex.lastIndex;
  }
  if (lastIndex < str.length) {
    result.push({ type: "text", value: str.slice(lastIndex) });
  }

  return result;
};

// ─── Count emojis in a string ─────────────────────────────────────────────────

const countEmojis = (str) => {
  if (!str) return 0;
  const emojiRegex =
    /(\p{Emoji_Presentation}|\p{Extended_Pictographic})(?:\u200D(?:\p{Emoji_Presentation}|\p{Extended_Pictographic})[\uFE0F\u20E3]?)*/gu;
  return (str.match(emojiRegex) || []).length;
};

// ─── Core mixed renderer ──────────────────────────────────────────────────────
// FIX: emojis now wrap correctly within maxX boundary
// Returns the FINAL y position after all content is drawn

const renderMixedTextLine = (
  pdf,
  text,
  startX,
  startY,
  maxX,
  lineHeight = 4.5,
  fontSize = 9,
  textColor = [40, 40, 40]
) => {
  const parts = splitString(String(text || ""));
  if (!parts.length) return startY;

  let xPosition = startX;
  let yPosition = startY;

  parts.forEach((part) => {
    if (part.type === "emoji") {
      const emojiChar = part.value;
      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d");

      const size = isFamilyEmojiFour(emojiChar) ? 40 : isFamilyEmoji(emojiChar) ? 40 : 35;
      canvas.width = size;
      canvas.height = size;

      ctx.font = `${size * 0.7}px 'Segoe UI Emoji', 'Apple Color Emoji', 'Noto Color Emoji', sans-serif`;
      ctx.clearRect(0, 0, size, size);
      ctx.fillText(emojiChar, 2, size * 0.82);

      const imgData = canvas.toDataURL("image/png");
      // FIX: use consistent emoji width for wrapping math
      const imgW = isFamilyEmojiFour(emojiChar) ? 6 : 4.5;
      const imgH = imgW;
      const gap = 0.8; // gap after each emoji

      // FIX: wrap to next line BEFORE placing if it would overflow
      if (xPosition !== startX && xPosition + imgW > maxX) {
        xPosition = startX;
        yPosition += lineHeight;
      }

      // Align emoji baseline with text: top = baseline - (imgH * 0.8)
      pdf.addImage(imgData, "PNG", xPosition, yPosition - imgH * 0.8, imgW, imgH);
      xPosition += imgW + gap;

    } else {
      pdf.setFont("helvetica", "normal");
      pdf.setFontSize(fontSize);
      pdf.setTextColor(...textColor);

      //const segmentW = maxX - startX;
      const remainingWidth = maxX - xPosition;

      const wrappedLines = pdf.splitTextToSize(
        part.value,
        xPosition === startX ? (maxX - startX) : remainingWidth
      );
      //const wrappedLines = pdf.splitTextToSize(part.value, segmentW);

      wrappedLines.forEach((line, li) => {
        if (li > 0) {
          xPosition = startX;
          yPosition += lineHeight;
        } else {
          //const lineW = pdf.getTextWidth(line);
          const lineW = pdf.getTextWidth(line);
          // if (xPosition + lineW > maxX) {
          //   xPosition = startX;
          //   yPosition += lineHeight;
          // }
          if (xPosition !== startX && xPosition + lineW > maxX) {
            xPosition = startX;
            yPosition += lineHeight;
          }
        }

        pdf.text(line, xPosition, yPosition);
        xPosition += pdf.getTextWidth(line);
      });
    }
  });

  return yPosition;
};

// ─── Estimate body height (emoji-aware) ──────────────────────────────────────
// FIX: accurately simulates the same wrap logic as renderMixedTextLine

const estimateBodyHeight = (pdf, text, textAreaW, lineH) => {
  const parts = splitString(String(text || ""));
  if (!parts.length) return lineH;

  pdf.setFontSize(9);
  pdf.setFont("helvetica", "normal");

  let xPosition = 0;
  let lines = 1;

  parts.forEach((part) => {
    if (part.type === "emoji") {
      const imgW = isFamilyEmojiFour(part.value) ? 6 : 4.5;
      const gap = 0.8;
      const totalW = imgW + gap;

      // Wrap only if emoji genuinely doesn't fit on remaining space
      if (xPosition > 0 && xPosition + imgW > textAreaW) {
        xPosition = 0;
        lines++;
      }
      xPosition += totalW;

    } else {
      // Always wrap text against full textAreaW (splitTextToSize behaviour)
      const wrappedLines = pdf.splitTextToSize(part.value, textAreaW);

      wrappedLines.forEach((line, li) => {
        const lineW = pdf.getTextWidth(line);

        if (li > 0) {
          // Continuation lines: always a new line
          xPosition = 0;
          lines++;
        } else if (xPosition > 0 && xPosition + lineW > textAreaW) {
          // First segment line doesn't fit remaining space → new line
          xPosition = 0;
          lines++;
        }
        // If xPosition === 0 it's already on a fresh line — no extra increment

        xPosition += lineW;
      });
    }
  });

  // Minimum 1 line height
  return Math.max(lines, 1) * lineH;
};

// ─── Draw page header ─────────────────────────────────────────────────────────

const drawPageHeader = (pdf, inmateName, title, pageWidth) => {
  const margin = 12;
  const bannerHeight = 22;

  pdf.setFillColor(28, 58, 100);
  pdf.rect(0, 0, pageWidth, bannerHeight, "F");

  pdf.setTextColor(255, 255, 255);
  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(13);
  pdf.text("Inmate Inbox", pageWidth / 2, 9, { align: "center" });

  pdf.setFontSize(9);
  pdf.setFont("helvetica", "normal");
  pdf.text(sanitize(title), pageWidth / 2, 17, { align: "center" });

  pdf.setFontSize(8.5);
  pdf.text(
    sanitize("Name: " + inmateName),
    pageWidth - margin,
    bannerHeight - 3,
    { align: "right" }
  );

  return bannerHeight + 8;
};

// ─── Group messages by subject ────────────────────────────────────────────────

const groupBySubject = (messages) => {
  const groups = [];
  const seen = new Map();
  messages.forEach((msg) => {
    const key = String(msg.MessageSubject || "No Subject");
    if (!seen.has(key)) {
      seen.set(key, groups.length);
      groups.push({ subject: key, messages: [msg] });
    } else {
      groups[seen.get(key)].messages.push(msg);
    }
  });
  return groups;
};

// ─── Draw subject pill (centered, side lines, multi-line for long subjects) ────────────────

const drawSubjectPill = (pdf, subject, y, pageWidth, margin) => {
  const label = `Subject: ${subject}`;

  pdf.setFontSize(9);
  pdf.setFont("helvetica", "bold");

  const padX = 10;
  const padY = 4;
  const lineH = 4.5;
  const maxPillW = pageWidth - margin * 2 - 24;

  const emojiRe = /(\p{Emoji_Presentation}|\p{Extended_Pictographic})(?:\u200D(?:\p{Emoji_Presentation}|\p{Extended_Pictographic})[\uFE0F\u20E3]?)*/gu;
  const labelForWidth = label.replace(emojiRe, "WW");
  const scale = 1 / pdf.internal.scaleFactor;
  const singleLineW = pdf.getStringUnitWidth(labelForWidth) * 9 * scale + padX * 2;

  const pillW = Math.min(singleLineW, maxPillW);
  const contentW = pillW - padX * 2;

  const contentH = estimateBodyHeight(pdf, label, contentW, lineH);
  const pillH = Math.max(padY + contentH + padY, 9);

  const pillX = (pageWidth - pillW) / 2;
  const lineMidY = y + pillH / 2;

  pdf.setDrawColor(180, 195, 215);
  pdf.setLineWidth(0.4);
  pdf.line(margin, lineMidY, pillX - 4, lineMidY);
  pdf.line(pillX + pillW + 4, lineMidY, pageWidth - margin, lineMidY);

  pdf.setFillColor(28, 58, 100);
  pdf.roundedRect(pillX, y, pillW, pillH, 3.5, 3.5, "F");

  renderMixedTextLine(
    pdf,
    label,
    pillX + padX,
    y + padY + lineH - 1,
    pillX + pillW - padX,
    lineH,
    9,
    [255, 255, 255]
  );

  return y + pillH + 4;
};

// ─── Draw tick ────────────────────────────────────────────────────────────────

const drawTick = (pdf, x, y, isRead) => {
  const tickColor = isRead ? [52, 183, 241] : [160, 160, 160];
  pdf.setDrawColor(...tickColor);
  pdf.setLineWidth(0.6);
  pdf.line(x, y + 1.2, x + 1.2, y + 2.4);
  pdf.line(x + 1.2, y + 2.4, x + 3.4, y - 0.4);
  if (isRead) {
    pdf.line(x + 2, y + 1.2, x + 3.2, y + 2.4);
    pdf.line(x + 3.2, y + 2.4, x + 5.4, y - 0.4);
  }
};

// ─── Draw one chat bubble ─────────────────────────────────────────────────────

const drawBubble = (pdf, msg, startY, pageWidth, margin, inmateName) => {
  const isPublicUser = msg.Sender === "Public User";

  const senderLabel = sanitize(
    isPublicUser
      ? msg.PublicUserFullName || inmateName || "Public User"
      : msg.InmateFullName || "Inmate"
  );

  const dateLabel = sanitize(msg.MessageSentDate || "");
  const statusLabel = sanitize(msg.ReceiverRead || "");
  const bodyText = String(msg.MessageBody || "");
  const isRead = statusLabel.toLowerCase() === "read";

  const avatarR = 5.5;
  const avatarDiam = avatarR * 2;
  const avatarGap = 3;
  const bubbleMaxW = pageWidth * 0.55;
  const padX = 6;
  const padY = 2;
  const lineH = 4.5;
  const headerH = 6;
  const dividerGap = 4;
  const footerH = 0;        // reserved space for date + tick row
  const textAreaW = bubbleMaxW - padX * 2;

  const bodyH = estimateBodyHeight(pdf, bodyText, textAreaW, lineH);

  // Total: topPad + header + dividerGap + body + footerH + bottomPad
  const bubbleH = padY + headerH + dividerGap + bodyH + footerH + padY;

  const bubbleX = isPublicUser
    ? pageWidth - margin - avatarDiam - avatarGap - bubbleMaxW
    : margin + avatarDiam + avatarGap;

  const avatarCX = isPublicUser
    ? pageWidth - margin - avatarR
    : margin + avatarR;
  const avatarCY = startY + avatarR + 2;

  // Avatar
  pdf.setFillColor(...(isPublicUser ? [28, 58, 100] : [13, 122, 110]));
  pdf.circle(avatarCX, avatarCY, avatarR, "F");
  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(9.5);
  pdf.setTextColor(255, 255, 255);
  pdf.text(getInitials(senderLabel), avatarCX, avatarCY + 2.0, { align: "center" });

  // Bubble background + border
  pdf.setFillColor(...(isPublicUser ? [210, 228, 250] : [245, 245, 245]));
  pdf.roundedRect(bubbleX, startY, bubbleMaxW, bubbleH, 4, 4, "F");
  pdf.setDrawColor(...(isPublicUser ? [148, 185, 228] : [200, 200, 200]));
  pdf.setLineWidth(0.3);
  pdf.roundedRect(bubbleX, startY, bubbleMaxW, bubbleH, 4, 4, "S");

  let cy = startY + padY;
  const tx = bubbleX + padX;
  const maxX = bubbleX + bubbleMaxW - padX;

  // Sender name
  pdf.setFontSize(8.5);
  pdf.setFont("helvetica", "bold");
  pdf.setTextColor(...(isPublicUser ? [28, 76, 150] : [13, 100, 90]));
  pdf.text(senderLabel, tx, cy + 3);
  cy += headerH;

  // Divider
  pdf.setDrawColor(200, 212, 228);
  pdf.setLineWidth(0.2);
  pdf.line(tx, cy, maxX, cy);
  cy += 4;

  // Body (emoji-aware renderer — stays within maxX)
  renderMixedTextLine(
    pdf,
    bodyText,
    tx,
    cy,
    maxX,
    lineH,
    9,
    [40, 40, 40]
  );

  // Footer: date + tick always anchored to bottom of bubble
  const bubbleRight = bubbleX + bubbleMaxW;

  // FIX: explicitly reset font to normal BEFORE measuring/drawing date
  // (prevents bold bleed from sender name or other prior setFont calls)
  pdf.setFontSize(7);
  pdf.setFont("helvetica", "normal");
  pdf.setTextColor(120, 120, 120);

  // FIX: measure date width so tick sits immediately after it on the SAME line
  const tickW = isRead ? 6.5 : 4.5;
  const tickGap = 1.5;

  const headerY = startY + padY + 3; // same vertical zone as sender name

  const tickX = bubbleRight - padX - tickW;
  const dateX = tickX - tickGap;

  pdf.text(dateLabel, dateX, headerY, { align: "right" });
  drawTick(pdf, tickX, headerY - 1.5, isRead);

  return startY + bubbleH + 10;
};

// ─── Main export ─────────────────────────────────────────────────────────────

const generateNewPublicUserMessagePDF = (
  inmateName,
  messageConversation,
  title
) => {
  if (!Array.isArray(messageConversation)) {
    console.error("generatePublicUserMessagePDF: expected an array.");
    return;
  }

  const pdf = new jsPDF({ compress: true });
  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();
  const margin = 12;

  pdf.setProperties({ title: title || "Inmate Inbox - Message Conversation" });

  const groups = groupBySubject(messageConversation);

  groups.forEach((group, gi) => {
    let currentY;

    if (gi === 0) {
      currentY = drawPageHeader(pdf, inmateName, title, pageWidth);
    } else {
      addWatermark(pdf, pageWidth, pageHeight);
      pdf.addPage();
      currentY = drawPageHeader(pdf, inmateName, title, pageWidth);
    }

    currentY = drawSubjectPill(pdf, group.subject, currentY, pageWidth, margin);

    group.messages.forEach((msg) => {
      // Match drawBubble exactly: bubbleMaxW = pageWidth * 0.55, padX = 6
      const bubbleMaxW = pageWidth * 0.55;
      const padX = 6;
      const padY = 2;
      const headerH = 6;
      const dividerGap = 4;
      const footerH = 0;
      const lineH = 4.5;
      const textAreaW = bubbleMaxW - padX * 2;   // ← was pageWidth * 0.60 - 12

      const bodyH = estimateBodyHeight(pdf, String(msg.MessageBody || ""), textAreaW, lineH);

      // Mirror drawBubble's bubbleH formula exactly, plus inter-bubble gap
      const bubbleH = padY + headerH + dividerGap + bodyH + footerH + padY;
      const estimatedH = bubbleH + 10 + 5; // +10 gap, +8 safe margin

      if (currentY + estimatedH > pageHeight - 16) {
        addWatermark(pdf, pageWidth, pageHeight);
        pdf.addPage();
        currentY = drawPageHeader(pdf, inmateName, title, pageWidth);
      }

      currentY = drawBubble(pdf, msg, currentY, pageWidth, margin, inmateName);
    });
  });

  // Watermark + page numbers
  const totalPages = pdf.internal.getNumberOfPages();
  for (let i = 1; i <= totalPages; i++) {
    pdf.setPage(i);
    addWatermark(pdf, pageWidth, pageHeight);
    addPageNumbers(pdf, i, totalPages, pageWidth, pageHeight);
  }

  pdf.save(`${sanitize(title) || "MessageConversation"}.pdf`);
};

export default generateNewPublicUserMessagePDF;